import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ReCaptchaV3Service, ScriptService } from 'ngx-captcha';
import { CAPTCHA_CONFIG } from './captcha.config';
import { CaptchaConfig } from './captcha.interface';
import type { CaptchaAction } from './captcha.types';

@UntilDestroy({ className: 'CaptchaService' })
@Injectable({
  providedIn: 'root',
})
export class CaptchaService {
  private static readonly badgeHideStyleId = 'fl-grecaptcha-badge-hide';

  private config = inject<CaptchaConfig>(CAPTCHA_CONFIG, { optional: true });
  private platformId = inject(PLATFORM_ID);
  private reCaptchaV3Service = inject(ReCaptchaV3Service);
  private scriptService = inject(ScriptService);

  /**
   * reCAPTCHA shows a floating badge at the lower right
   * Corner which needs custom handling to be hidden.
   */
  private hideRecaptchaBadge(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (document.getElementById(CaptchaService.badgeHideStyleId)) {
      return;
    }

    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');
    style.id = CaptchaService.badgeHideStyleId;
    style.appendChild(
      document.createTextNode(`
      .grecaptcha-badge {
        display: none;
      }`),
    );
    head.appendChild(style);
  }

  /**
   * Loads the reCAPTCHA v3 client script (same URL/ngx-captcha path as token minting)
   * without calling grecaptcha.execute. Call while the login form is visible so the first
   * submit only pays for execute + network, not script download.
   */
  preloadRecaptchaV3Client(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const siteKey = this.config?.recaptchaV3PublicKey ?? '';
    if (!siteKey) {
      return;
    }

    this.hideRecaptchaBadge();

    this.scriptService.registerCaptchaScript(
      { useGlobalDomain: true },
      siteKey,
      () => {
        // Script loaded; token is obtained in getCaptchaV3Token via executeAsPromise.
      },
    );
  }

  getCaptchaV3Token(action: CaptchaAction): Promise<string | undefined> {
    if (!isPlatformBrowser(this.platformId)) {
      // Don't execute if running on the server
      return Promise.resolve(undefined);
    }

    this.hideRecaptchaBadge();

    return this.reCaptchaV3Service.executeAsPromise(
      this.config?.recaptchaV3PublicKey ?? '',
      action,
      { useGlobalDomain: true },
    );
  }
}
